// Size slider with colour-coded free / paid zones.
//
// Two visual modes:
//
//   Flat (non-tiered placements): single mint band across the whole track.
//     The placement stays free at every size from 30mm to its maxWidthMm.
//
//   Tiered (centre chest + back): multi-band gradient driven by Config.sizeTiers.
//     Each tier renders as a coloured band sized proportionally to its
//     [minMm, maxMm] range. A row of tier-cells under the track names each
//     band's range + price. The first 0-cents tier is the free zone.
//
// Tier-mode membership is owned by `isTieredPlacement` in pricing.ts so there's
// a single source of truth for "does this placement get oversize pricing?"

import type { Config, Placement, SizeTier } from "../types";
import { effectiveTiersFor, tierForSize } from "../pricing";

export function SizeSlider({
  placement,
  sizeMm,
  config,
  onChange,
}: {
  placement: Placement;
  sizeMm: number;
  config: Config;
  onChange: (next: number) => void;
}) {
  // Phase 17b: per-placement tiers (from Template) override the global
  // sizeTiers + slug-based legacy mode. The helper does the resolution.
  const tiers = effectiveTiersFor(placement, config);
  // Phase 85: only show the multi-band tier UI when a tier actually
  // COSTS something. A placement whose tiers are all free (e.g. the tote,
  // free up to its max) was rendering confusing multi-band zones with
  // conflicting per-tier labels ("FREE UP TO 50MM" vs "<=75mm Free").
  // All-free => flat "free at every size" band, one consistent message.
  const tierMode = tiers.some((t) => t.priceCents > 0);
  const tierApplied = tierForSize(sizeMm, tiers, tierMode);
  const min = 30;
  // Phase 67: template-level lockedMaxMm clamps the slider's right
  // bound even further than the placement's catalogue cap. Used by
  // one-off templates that want a single uniform max (e.g. pet-varsity
  // = 300mm on every spot regardless of master library defaults).
  const cap = (config.product as { lockedMaxMm?: number | null }).lockedMaxMm
    ? Math.min(placement.maxWidthMm, (config.product as { lockedMaxMm?: number | null }).lockedMaxMm!)
    : placement.maxWidthMm;
  const max = Math.max(min + 10, cap);

  // For non-tiered placements we render a flat green band. For tiered ones
  // we render a coloured band per SizeTier row, sized proportionally.
  const bands = tierMode
    ? buildBands(tiers, min, max)
    : [{ leftPct: 0, widthPct: 100, kind: "free" as const, label: "Free" }];

  return (
    <div className="rk-pc-slider">
      <div className="rk-pc-slider__row">
        <span className="rk-pc-slider__value">
          {sizeMm}
          <small>mm</small>
        </span>
        <span
          className={`rk-pc-slider__tier-label${tierApplied.feeCents > 0 ? " is-fee" : ""}`}
        >
          {tierApplied.feeCents > 0 ? `+$${(tierApplied.feeCents / 100).toFixed(0)}` : "Free"}
          {/* Phase 85: all-free placements show one consistent "up to Nmm"
              instead of a stale per-tier label. */}
          <small>{tierMode ? tierApplied.label : `up to ${max}mm`}</small>
        </span>
      </div>
      <div className="rk-pc-slider__wrap">
        <div className="rk-pc-slider__track">
          {bands.map((b, i) => (
            <div
              key={i}
              className={`rk-pc-slider__band is-${b.kind}`}
              style={{ left: `${b.leftPct}%`, width: `${b.widthPct}%` }}
            />
          ))}
        </div>
        {/* Split markers between bands so the tier boundaries are visible
            even when adjacent bands have similar tones. */}
        {tierMode &&
          bands.slice(0, -1).map((b, i) => (
            <div
              key={`split-${i}`}
              className="rk-pc-slider__split"
              style={{ left: `${b.leftPct + b.widthPct}%` }}
            />
          ))}
        <input
          type="range"
          className="rk-pc-slider__input"
          min={min}
          max={max}
          step={5}
          value={sizeMm}
          onChange={(e) => onChange(Number(e.target.value))}
          aria-label={`Embroidery size in millimetres for ${placement.name}`}
        />
      </div>
      {tierMode ? (
        <div className="rk-pc-slider__tiers">
          {tiers.map((t, i) => (
            <button
              type="button"
              key={t.id}
              className={`rk-pc-slider__tier-cell is-${bandKindForTier(t, i)}${
                sizeMm <= t.maxMm && !tiers.slice(0, i).some((tt) => sizeMm <= tt.maxMm)
                  ? " is-active"
                  : ""
              }`}
              onClick={() => onChange(t.maxMm)}
              aria-label={`Set embroidery size to ${t.maxMm}mm`}
            >
              <small>≤{t.maxMm}mm</small>
              <strong>{t.priceCents === 0 ? "Free" : `+$${(t.priceCents / 100).toFixed(0)}`}</strong>
            </button>
          ))}
        </div>
      ) : (
        <div className="rk-pc-slider__legend">
          <span>
            <strong>{min}mm</strong>
          </span>
          <span className="rk-pc-slider__legend-note">{placement.name} stays free at every size</span>
          <span>
            <strong>{max}mm</strong>
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Band layout ──────────────────────────────────────────────────────────

type BandKind = "free" | "paid-1" | "paid-2" | "paid-3";

type Band = {
  leftPct: number;
  widthPct: number;
  kind: BandKind;
  label: string;
};

// Build proportional bands from the tier rows. The slider's domain is
// [min, max]; each tier covers [tier.minMm, tier.maxMm]. Tiers with priceCents
// = 0 use the "free" colour; paid tiers cycle paid-1/2/3 for visual variety
// when there are multiple paid bands.
function buildBands(tiers: SizeTier[], min: number, max: number): Band[] {
  if (tiers.length === 0) return [];
  const range = max - min;
  if (range <= 0) return [];
  let paidIndex = 0;
  return tiers.map((t) => {
    const lo = Math.max(min, t.minMm);
    const hi = Math.min(max, t.maxMm);
    const leftPct = Math.max(0, ((lo - min) / range) * 100);
    const widthPct = Math.max(0, ((hi - lo) / range) * 100);
    const kind: BandKind =
      t.priceCents === 0 ? "free" : (`paid-${Math.min(3, ++paidIndex)}` as BandKind);
    return { leftPct, widthPct, kind, label: t.label };
  });
}

function bandKindForTier(t: SizeTier, i: number): BandKind {
  if (t.priceCents === 0) return "free";
  // Count paid index by looking at how many priceCents>0 tiers come before
  // this one (plus 1 for self). Caps at paid-3 visually.
  // i is the tier's position in the sorted array. Paid bands tend to be
  // contiguous after free, so just clamp by index for simplicity.
  return (`paid-${Math.min(3, i)}` as BandKind);
}
